/**
 * Logique client alignée sur `firestore.rules` (harmonistBeta*, harmonistBetaFullCloudAccess).
 * Toute modification des rules institutionnelles / suffixes doit être reflétée ici.
 */
import { deleteDoc, doc, getDoc, onSnapshot, setDoc } from 'firebase/firestore'
import { db } from '../firebase'

/** Domaines institutionnels codés en dur dans firestore.rules → harmonistBetaEmailEntitled */
const HARMONIST_BETA_INSTITUTIONAL_EMAIL_TESTS = [
  /@hes-so\.ch$/i,
  /@etu\.hesge\.ch$/i,
  /@hesge\.ch$/i,
  /@hemu-cl\.ch$/i,
]

/**
 * @param {string} emailStr
 * @returns {string}
 */
export function harmonistEmailDocIdFromEmail(emailStr) {
  if (typeof emailStr !== 'string' || !emailStr) return ''
  return emailStr.toLowerCase().trim().replace(/\//g, '__')
}

function matchesInstitutionalEmailLower(emailLower) {
  if (!emailLower) return false
  return HARMONIST_BETA_INSTITUTIONAL_EMAIL_TESTS.some((re) => re.test(emailLower))
}

/**
 * Suffixe @domaine… aligné sur harmonistBetaSuffixStringSafe (rules).
 * @param {string} s
 */
function isConfigSuffixShapeOk(s) {
  if (typeof s !== 'string' || s.length < 3 || s.length > 80) return false
  return /^@[a-z0-9][a-z0-9._-]{1,78}$/i.test(s)
}

function emailEndsWithSuffix(emailLower, suffixLower) {
  if (!suffixLower || !isConfigSuffixShapeOk(suffixLower)) return false
  const suf = suffixLower.toLowerCase().trim()
  return emailLower.length >= suf.length && emailLower.slice(-suf.length) === suf
}

/**
 * @param {string} emailLower
 * @param {import('firebase/firestore').DocumentData | null | undefined} betaConfig
 * @param {boolean} whitelistDocExists
 */
export function computeHarmonistBetaEmailEntitled(emailLower, betaConfig, whitelistDocExists) {
  const e = (emailLower || '').toLowerCase().trim()
  if (!e) return false
  if (matchesInstitutionalEmailLower(e)) return true
  const suffixes = betaConfig?.emailSuffixes
  if (Array.isArray(suffixes)) {
    for (let i = 0; i < Math.min(suffixes.length, 20); i += 1) {
      const raw = suffixes[i]
      if (typeof raw !== 'string') continue
      if (emailEndsWithSuffix(e, raw)) return true
    }
  }
  const wid = harmonistEmailDocIdFromEmail(e)
  if (wid && whitelistDocExists) return true
  return false
}

/**
 * @param {{
 *   betaPrivateActive: boolean
 *   email: string | null | undefined
 *   userRole: string | null | undefined
 *   isAdminTeacherDoc: boolean
 *   betaConfig: import('firebase/firestore').DocumentData | null | undefined
 *   whitelistDocExists: boolean
 * }} p
 */
export function computeHasFullCloudAccess(p) {
  const {
    betaPrivateActive,
    email,
    userRole,
    isAdminTeacherDoc,
    betaConfig,
    whitelistDocExists,
  } = p
  if (!betaPrivateActive) return true
  if (userRole === 'teacher') return true
  if (isAdminTeacherDoc) return true
  const e = (email || '').toLowerCase().trim()
  if (computeHarmonistBetaEmailEntitled(e, betaConfig, whitelistDocExists)) return true
  return false
}

/**
 * @param {(data: import('firebase/firestore').DocumentData | null) => void} onData
 * @returns {import('firebase/firestore').Unsubscribe}
 */
export function subscribeHarmonistBetaConfig(onData) {
  const ref = doc(db, 'config', 'harmonistBeta')
  return onSnapshot(
    ref,
    (snap) => {
      onData(snap.exists() ? snap.data() : null)
    },
    (err) => {
      console.error('subscribeHarmonistBetaConfig:', err)
      onData(null)
    }
  )
}

/**
 * @param {string | null | undefined} email
 * @param {(exists: boolean) => void} onExists
 * @returns {import('firebase/firestore').Unsubscribe | null}
 */
export function subscribeBetaEmailWhitelistDoc(email, onExists) {
  const wid = harmonistEmailDocIdFromEmail(email || '')
  if (!wid) {
    onExists(false)
    return null
  }
  const ref = doc(db, 'betaEmailWhitelist', wid)
  return onSnapshot(
    ref,
    (snap) => {
      onExists(snap.exists())
    },
    (err) => {
      console.error('subscribeBetaEmailWhitelistDoc:', err)
      onExists(false)
    }
  )
}

/**
 * @param {string} uid
 * @returns {Promise<import('firebase/firestore').DocumentData | null>}
 */
export async function getBetaAccessRequest(uid) {
  if (!uid) return null
  try {
    const snap = await getDoc(doc(db, 'betaAccessRequests', uid))
    return snap.exists() ? snap.data() : null
  } catch (e) {
    console.error('getBetaAccessRequest:', e)
    return null
  }
}

/**
 * @param {string} uid
 * @param {(data: import('firebase/firestore').DocumentData | null) => void} onData
 * @returns {import('firebase/firestore').Unsubscribe}
 */
export function subscribeBetaAccessRequest(uid, onData) {
  if (!uid) {
    onData(null)
    return () => {}
  }
  const ref = doc(db, 'betaAccessRequests', uid)
  return onSnapshot(
    ref,
    (snap) => {
      onData(snap.exists() ? snap.data() : null)
    },
    (err) => {
      console.error('subscribeBetaAccessRequest:', err)
      onData(null)
    }
  )
}

/**
 * Retire la fiche côté élève pour renvoyer une demande (refus, ou statut approuvé sans accès effectif — aligné Harmonist / rules).
 * @param {string} uid
 */
export async function deleteOwnBetaAccessRequest(uid) {
  if (!uid) throw new Error('uid requis')
  await deleteDoc(doc(db, 'betaAccessRequests', uid))
}

/**
 * Crée une demande d’accès (règles : un doc par uid, statut pending, email = JWT).
 * Comportement aligné Harmonist Academy : si une fiche existe en « pending », erreur ;
 * si « approved » ou « rejected », suppression puis recréation.
 * @param {{ uid: string, email: string, message?: string }} params
 */
export async function submitBetaAccessRequest({ uid, email, message }) {
  if (!uid || !email) throw new Error('uid et email requis')
  const ref = doc(db, 'betaAccessRequests', uid)
  const existing = await getDoc(ref)
  if (existing.exists()) {
    const st = existing.data()?.status
    if (st === 'pending') {
      const err = new Error('Une demande est déjà en cours pour ce compte.')
      err.code = 'already_pending'
      throw err
    }
    if (st === 'approved' || st === 'rejected') {
      await deleteDoc(ref)
    } else {
      const err = new Error(
        'Une fiche de demande existe encore. Un administrateur peut la retirer depuis le hub.'
      )
      err.code = 'existing_request'
      throw err
    }
  }
  const payload = {
    uid,
    email,
    status: 'pending',
  }
  if (message != null && String(message).trim()) {
    payload.message = String(message).trim().slice(0, 500)
  }
  await setDoc(ref, payload)
}
